import { Button } from '@mui/material'
import { ValueLabelProps } from 'components/Common/ValueLabel/ValueLabel'
import { useBomItemDetailsDialog } from 'features/BomItemDetailsDialog/useBomItemDetailsDialog'
import { MoneyString } from 'model/Money'
import { BomItemType } from 'model/Project/BoMItemRow'
import { BomItemPointer } from 'model/Project/BomItemPointer'
import { useTranslation } from 'react-i18next'
import {
  MoneyDto,
  WorkingStepType,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { useAppSelector } from 'store/configureStore'
import { ValueLabelConnected } from '../store/components/ValueLabelConnected'
import { ProjectEditableStateSelector } from '../store/selectors/ProjectEditableStateSelector'
import { bomItemFinancialSelector } from '../store/selectors/bomItemFinancialSelector'
import { bomItemIsPurchasingPartSelector } from '../store/selectors/bomItemIsPurchasingPartSelector'
import { projectInBuyerViewSelector } from '../store/selectors/projectInBuyerViewSelector'

export type StyleProps = {
  fullWidth?: boolean
}

export const BomItemPricePerBatch = ({
  bomItemPointer,
  label,
  valueLabelProps,
  fullWidth = false,
  dataTestId,
}: {
  bomItemPointer: BomItemPointer
  label?: string
  valueLabelProps?: Partial<ValueLabelProps>
  fullWidth?: boolean
  dataTestId?: string
}) => {
  const { t } = useTranslation()
  const isPurchasingPart = useAppSelector(
    bomItemIsPurchasingPartSelector(bomItemPointer),
  )

  const projectIsEditable = useAppSelector(ProjectEditableStateSelector)
  const isBuyerView = useAppSelector(projectInBuyerViewSelector)

  const openDetailsDialog = useBomItemDetailsDialog()

  const handleOpenPurchasingPartDialog = () => {
    openDetailsDialog('articles', {
      bomItemPointer: bomItemPointer,
      workingStep: WorkingStepType.Purchasing,
    })
  }

  if (
    !isPurchasingPart ||
    !projectIsEditable ||
    isBuyerView ||
    bomItemPointer.type === BomItemType.partInstance ||
    bomItemPointer.type === BomItemType.assemblyInstance
  ) {
    return (
      <ValueLabelConnected
        propertySelector={(state) =>
          MoneyString(
            bomItemFinancialSelector(
              bomItemPointer,
              'salesPricePerItem',
            )(state) as MoneyDto,
          ) || '-'
        }
        showEmptyValue={isBuyerView ? false : true}
        hiddenFallbackComponent={'div'}
        dataTestId={dataTestId}
        valueLabelProps={{
          label: label || t('project:price-per-batch'),
          valueProps: {
            style: {
              width: fullWidth ? '100%' : '90px',
              fontVariantNumeric: 'tabular-nums',
            },
          },
          ...valueLabelProps,
        }}
      />
    )
  }

  return (
    <Button
      sx={{
        padding: (theme) => theme.spacing(0.5, 2.5, 0, 0.5),
        flex: (theme) => `1 1 ${theme.spacing(5)}`,
        justifyContent: 'flex-end',
        transform: 'translate(20px, -4px)',
      }}
      onClick={handleOpenPurchasingPartDialog}
      data-testid={dataTestId}
    >
      <ValueLabelConnected
        propertySelector={(state) =>
          MoneyString(
            bomItemFinancialSelector(
              bomItemPointer,
              'salesPricePerItem',
            )(state) as MoneyDto,
          ) || '-'
        }
        valueLabelProps={{
          label: label || t('project:price-per-batch'),
          valueProps: {
            style: {
              width: fullWidth ? '100%' : '90px',
              cursor: 'inherit',
              fontVariantNumeric: 'tabular-nums',
            },
          },
          ...valueLabelProps,
        }}
      />
    </Button>
  )
}
